/**
 * Tabela da fila do comprador (ADR-0078) — no padrão do Excel.
 *
 * - Visão POR REQUISIÇÃO (padrão) ou POR ITEM (uma linha por item, como a
 *   planilha que o comprador usava).
 * - Cada coluna ordena (clique no nome) e filtra por valores (funil).
 * - A pessoa escolhe quais colunas ver e SALVA a visualização como padrão
 *   (preferência do usuário, no servidor: vale em qualquer computador).
 *
 * Recebe as requisições JÁ filtradas pelos filtros do topo da página; os
 * funis daqui refinam dentro desse recorte.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiColumns, FiList, FiPackage, FiRotateCcw } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import TabelaExcel from "../../../components/TabelaExcel";
import { useAuth } from "../../../context/AuthContext";
import { useTabelaPreferenciaUI } from "../../../hooks/useTabelaPreferencia";
import { listarItensDaFila } from "../../../services/compras";
import {
  dePreferencia, normalizarColunas, opcoesDaColuna, ordenarLinhas, paraPreferencia,
  passaNosFiltros, proximaOrdenacao,
} from "../../../utils/tabelaExcel";
import { Carregando } from "../_shared/Estados";
import { PageBtn, TableFooter, ToggleBtn, ViewToggle } from "../_shared/quadro/styles";
import {
  CATALOGO, CHAVE_PREFERENCIA, MODO_ITEM, MODO_REQUISICAO, modoValido,
} from "./colunasFila";
import { PrazoCell, ProgressoItens, StatusPill, TagOrigem } from "./ui";

const POR_PAGINA = 50;

function estadoInicial(modo) {
  return { colunas: [...CATALOGO[modo].padrao], filtros: {}, ordenacao: null };
}

function SeletorColunas({ catalogo, padrao, visiveis, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (!ref.current?.contains(e.target)) setAberto(false); };
    const esc = (e) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fora); document.removeEventListener("keydown", esc); };
  }, [aberto]);

  const marcadas = new Set(visiveis);
  const alternar = (key) => {
    const prox = new Set(marcadas);
    if (prox.has(key)) prox.delete(key); else prox.add(key);
    onChange(normalizarColunas([...prox], catalogo, padrao));
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="btn btn--secondary btn--sm" aria-expanded={aberto} onClick={() => setAberto((v) => !v)}>
        <FiColumns size={13} /> Colunas <span style={{ color: "var(--ink-4)" }}>{visiveis.length}/{catalogo.length}</span>
      </button>
      {aberto ? (
        <div
          role="dialog"
          aria-label="Colunas visíveis"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 30, width: 280, maxHeight: 400,
            overflowY: "auto", padding: 10, background: "var(--bg-elevated, var(--surface))",
            border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow-3)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <strong style={{ fontSize: 12.5 }}>Colunas visíveis</strong>
            <span style={{ display: "inline-flex", gap: 4 }}>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => onChange(catalogo.map((c) => c.key))}>Todas</button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => onChange([...padrao])}>Padrão</button>
            </span>
          </div>
          {catalogo.map((c) => (
            <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", fontSize: 12.5, cursor: c.fixa ? "default" : "pointer", color: "var(--ink)" }}>
              <input type="checkbox" checked={marcadas.has(c.key)} disabled={c.fixa} onChange={() => alternar(c.key)} />
              {c.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Células ricas: só a aparência; filtro e ordem usam o `valor` da coluna. */
const RENDER = {
  [MODO_REQUISICAO]: {
    status: (r) => <StatusPill situacao={r.situacao} />,
    origem: (r) => <TagOrigem origem={r.origem} label={r.origem_label} curta />,
    itens: (r) => <ProgressoItens req={r} compacto />,
    prazo: (r) => <PrazoCell req={r} />,
  },
  [MODO_ITEM]: {
    status: (i) => <StatusPill situacao={i.requisicao_situacao} />,
    origem: (i) => <TagOrigem origem={i.origem} label={i.origem_label} curta />,
  },
};

export default function TabelaComprador({
  requisicoes, selecionadas, onToggleSel, onToggleTodas, onAbrir, podeGerir, versao = 0, onLinhasVisiveis,
}) {
  const { request } = useAuth();
  const { getUI, setUI, flush, estado: estadoPref } = useTabelaPreferenciaUI();
  const [modo, setModo] = useState(MODO_REQUISICAO);
  const [visoes, setVisoes] = useState(() => ({
    [MODO_REQUISICAO]: estadoInicial(MODO_REQUISICAO), [MODO_ITEM]: estadoInicial(MODO_ITEM),
  }));
  const [salva, setSalva] = useState(null);
  const [itens, setItens] = useState(null);
  const [erroItens, setErroItens] = useState("");
  const [pagina, setPagina] = useState(1);
  const hidratou = useRef(false);
  const mexeu = useRef(false);

  // Aplica a visualização salva UMA vez, quando a preferência carrega — sem
  // atropelar o que a pessoa já mexeu enquanto carregava.
  useEffect(() => {
    if (hidratou.current || estadoPref === "loading") return;
    hidratou.current = true;
    if (estadoPref !== "pronto") return;
    const pref = getUI()?.[CHAVE_PREFERENCIA];
    if (!pref) return;
    setSalva(pref);
    if (mexeu.current) return;
    setModo(modoValido(pref.modo));
    setVisoes({
      [MODO_REQUISICAO]: dePreferencia(pref[MODO_REQUISICAO], CATALOGO[MODO_REQUISICAO].colunas, CATALOGO[MODO_REQUISICAO].padrao),
      [MODO_ITEM]: dePreferencia(pref[MODO_ITEM], CATALOGO[MODO_ITEM].colunas, CATALOGO[MODO_ITEM].padrao),
    });
  }, [estadoPref, getUI]);

  // Itens: só busca quando a visão por item é aberta, e de novo quando a
  // fila muda (`versao`).
  useEffect(() => {
    if (modo !== MODO_ITEM) return undefined;
    let vivo = true;
    setErroItens("");
    listarItensDaFila(request, { incluirCanceladas: true })
      .then((r) => { if (vivo) setItens(Array.isArray(r) ? r : r?.results || []); })
      .catch((e) => { if (vivo) { setItens([]); setErroItens(e?.payload?.detalhe || e?.message || "Não foi possível carregar os itens."); } });
    return () => { vivo = false; };
  }, [modo, request, versao]);

  const { colunas: catalogo, padrao } = CATALOGO[modo];
  const visao = visoes[modo];
  const mudar = (parcial) => {
    mexeu.current = true;
    setPagina(1);
    setVisoes((v) => ({ ...v, [modo]: { ...v[modo], ...parcial } }));
  };

  const colunas = useMemo(
    () => catalogo
      .filter((c) => visao.colunas.includes(c.key))
      .map((c) => ({ ...c, render: RENDER[modo][c.key] })),
    [catalogo, visao.colunas, modo],
  );

  // Universo: as requisições que passam nos filtros do topo da página.
  const universo = useMemo(() => {
    if (modo === MODO_REQUISICAO) return requisicoes;
    const ids = new Set(requisicoes.map((r) => r.id));
    return (itens || []).filter((i) => ids.has(i.requisicao));
  }, [modo, requisicoes, itens]);

  const linhas = useMemo(
    () => ordenarLinhas(universo.filter((l) => passaNosFiltros(l, visao.filtros, catalogo)), visao.ordenacao, catalogo),
    [universo, visao.filtros, visao.ordenacao, catalogo],
  );

  useEffect(() => { onLinhasVisiveis?.({ modo, colunas, linhas }); }, [modo, colunas, linhas, onLinhasVisiveis]);

  const paginas = Math.max(1, Math.ceil(linhas.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, paginas);
  const visiveis = linhas.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

  const atual = {
    modo,
    [MODO_REQUISICAO]: paraPreferencia(visoes[MODO_REQUISICAO], CATALOGO[MODO_REQUISICAO].colunas, CATALOGO[MODO_REQUISICAO].padrao),
    [MODO_ITEM]: paraPreferencia(visoes[MODO_ITEM], CATALOGO[MODO_ITEM].colunas, CATALOGO[MODO_ITEM].padrao),
  };
  const fabrica = {
    modo: MODO_REQUISICAO,
    [MODO_REQUISICAO]: paraPreferencia(estadoInicial(MODO_REQUISICAO), CATALOGO[MODO_REQUISICAO].colunas, CATALOGO[MODO_REQUISICAO].padrao),
    [MODO_ITEM]: paraPreferencia(estadoInicial(MODO_ITEM), CATALOGO[MODO_ITEM].colunas, CATALOGO[MODO_ITEM].padrao),
  };
  const mudouDoPadrao = JSON.stringify(atual) !== JSON.stringify(salva || fabrica);
  const nFiltros = Object.values(visao.filtros).filter(Boolean).length;

  async function salvarComoPadrao() {
    setUI({ [CHAVE_PREFERENCIA]: atual });
    await flush({ propagarErro: true });
    setSalva(atual);
  }

  function voltarAoPadrao() {
    const base = salva || fabrica;
    mexeu.current = true;
    setPagina(1);
    setModo(modoValido(base.modo));
    setVisoes({
      [MODO_REQUISICAO]: dePreferencia(base[MODO_REQUISICAO], CATALOGO[MODO_REQUISICAO].colunas, CATALOGO[MODO_REQUISICAO].padrao),
      [MODO_ITEM]: dePreferencia(base[MODO_ITEM], CATALOGO[MODO_ITEM].colunas, CATALOGO[MODO_ITEM].padrao),
    });
  }

  const selecao = podeGerir && modo === MODO_REQUISICAO
    ? { selecionadas, onToggle: onToggleSel, onToggleTodas }
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <ViewToggle role="tablist" aria-label="Linhas da tabela">
          <ToggleBtn role="tab" aria-selected={modo === MODO_REQUISICAO} $on={modo === MODO_REQUISICAO} onClick={() => { mexeu.current = true; setPagina(1); setModo(MODO_REQUISICAO); }}>
            <FiList size={13} /> Por requisição
          </ToggleBtn>
          <ToggleBtn role="tab" aria-selected={modo === MODO_ITEM} $on={modo === MODO_ITEM} onClick={() => { mexeu.current = true; setPagina(1); setModo(MODO_ITEM); }}>
            <FiPackage size={13} /> Por item
          </ToggleBtn>
        </ViewToggle>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {linhas.length} {modo === MODO_ITEM ? (linhas.length === 1 ? "item" : "itens") : (linhas.length === 1 ? "requisição" : "requisições")}
          {nFiltros ? ` · ${nFiltros} coluna${nFiltros === 1 ? "" : "s"} filtrada${nFiltros === 1 ? "" : "s"}` : ""}
        </span>
        <span style={{ flex: 1 }} />
        {nFiltros ? (
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => mudar({ filtros: {} })}>Limpar filtros das colunas</button>
        ) : null}
        {mudouDoPadrao ? (
          <>
            <button type="button" className="btn btn--secondary btn--sm" onClick={voltarAoPadrao} title="Voltar à visualização salva">
              <FiRotateCcw size={12} /> Desfazer
            </button>
            <AsyncButton variant="secondary" onClick={salvarComoPadrao} successMessage="Visualização salva como padrão">
              Salvar como padrão
            </AsyncButton>
          </>
        ) : null}
        <SeletorColunas catalogo={catalogo} padrao={padrao} visiveis={visao.colunas} onChange={(keys) => mudar({ colunas: keys })} />
      </div>

      {erroItens ? <div role="alert" style={{ fontSize: 12, color: "var(--danger-text)" }}>{erroItens}</div> : null}
      {modo === MODO_ITEM && itens === null ? (
        <Carregando linhas={8} colunas={colunas.length} />
      ) : (
        <TabelaExcel
          tableId={`req-compra-${modo}`}
          colunas={colunas}
          linhas={visiveis}
          filtros={visao.filtros}
          ordenacao={visao.ordenacao}
          opcoesDe={(c) => opcoesDaColuna(c, universo, visao.filtros, catalogo)}
          onFiltro={(key, set) => mudar({ filtros: { ...visao.filtros, [key]: set } })}
          onOrdenar={(key) => mudar({ ordenacao: proximaOrdenacao(visao.ordenacao, key) })}
          onLinha={(l) => onAbrir(modo === MODO_ITEM ? l.requisicao : l.id)}
          selecao={selecao}
          atributosDaLinha={(l) => ({ "data-urgente": l.prioridade === "URGENTE" ? "true" : "false" })}
          vazio="Nada passa nos filtros das colunas."
        />
      )}

      {linhas.length > POR_PAGINA ? (
        <TableFooter>
          <span>{(paginaAtual - 1) * POR_PAGINA + 1}–{Math.min(linhas.length, paginaAtual * POR_PAGINA)} de {linhas.length}</span>
          <span style={{ display: "inline-flex", gap: 6 }}>
            <PageBtn type="button" disabled={paginaAtual <= 1} onClick={() => setPagina(paginaAtual - 1)}>Anterior</PageBtn>
            <PageBtn type="button" disabled={paginaAtual >= paginas} onClick={() => setPagina(paginaAtual + 1)}>Próxima</PageBtn>
          </span>
        </TableFooter>
      ) : null}
    </div>
  );
}
